/**
 * 62.64 (b): the frame's matrix walk skips what is hidden. Each draw, three.js walks the whole scene graph to bring every
 * world matrix up to date (`scene.updateMatrixWorld()`, inside `renderer.render`), hidden or not — and at 32 a side most of
 * the graph is hidden: of ~6,900 objects, ~5,900 sat under a hidden object (each person's full figure while it draws as a
 * silhouette or in the crowd's batch, its silhouette while it draws in full, and the classic unit model the person stands
 * in for). An object marked here (`skipWhenHidden`) returns from that walk while it is hidden, so its subtree is not walked.
 *
 * Exact for everything drawn: a hidden object's subtree is not drawn, and every object marked here has a parent that
 * updates its own matrix each frame (a person's holder or root, the scene), which forces the full subtree's walk the
 * frame the object shows again. Outside the draw's walk a marked object behaves as three's: `getWorldPosition` and
 * `updateWorldMatrix` (a muzzle, an anchor) climb and compute as ever, and a forced `updateMatrixWorld(true)` made
 * outside the draw (the ragdoll's start, the crowd's bake) is never skipped. Presentation only.
 */
import * as THREE from 'three';

let inDrawWalk = false;

/** Run `draw` (the frame's render) with marked hidden objects skipped by its matrix walk. */
export function drawSkippingHidden(draw: () => void): void {
  inDrawWalk = true;
  try { draw(); } finally { inDrawWalk = false; }
}

const walk = THREE.Object3D.prototype.updateMatrixWorld;
function updateUnlessHiddenInDraw(this: THREE.Object3D, force?: boolean): void {
  if (inDrawWalk && !this.visible) return;
  walk.call(this, force);
}

/** Mark `o`: while it is hidden, the draw's matrix walk leaves its subtree (see the header). Idempotent. */
export function skipWhenHidden(o: THREE.Object3D): void {
  o.updateMatrixWorld = updateUnlessHiddenInDraw;
}
